import * as stylex from '@stylexjs/stylex';
import { useForm, useStore } from '@tanstack/react-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useId } from 'react';
import { ListLayout, Virtualizer, type Key } from 'react-aria-components';
import { literal as vLiteral } from 'valibot';

import { NonUSDoctorSpecialtyEnum, type components } from '@/libs/openapi';
import {
  Button,
  Heading,
  LabeledInput,
  PROGRESS_INDICATOR_MIN_DELAY_DURATION,
  Text,
  TextField,
  minDelay,
  SegmentedButton,
  SegmentedButtonGroup,
  View,
  ComboBox,
  Popover,
  ListBox,
  ListBoxItem,
  Icon,
  Hint,
  TextButton,
  Checkbox,
  LIST_BOX_ITEM_SLOTS,
  SKELETON_MIN_DELAY_DURATION,
  useDebounce,
} from '@/libs/serenity-ui';
import { breakpoints } from '@/libs/serenity-ui/constants.stylex';
import { $ } from '@/libs/serenity-ui/tokens.stylex';
import { RouterLink } from '@/libs/tanstack-router';
import { locationQueries } from '@/resources/location/location.api';
import { profileMutations, profileQueries } from '@/resources/profile/profile.api';
import { ProfileSchema } from '@/resources/profile/profile.schema';
import { specialtyQuery } from '@/resources/specialty/specialty.api';
import { tryCatch } from '@/utils/try-catch.util';

export const Route = createFileRoute('/_auth/create-profile')({
  component: RouteComponent,
  loader: async ({ context }) => {
    const { data: profile } = await tryCatch(context.queryClient.ensureQueryData(profileQueries.get));

    if (!sessionStorage.getItem('google_user_info') || profile) {
      throw redirect({ to: '/' });
    }
  },
});

function RouteComponent() {
  const id = useId();
  const navigate = Route.useNavigate();

  const createProfileMutation = useMutation(profileMutations.create);

  const form = useForm({
    defaultValues: {
      full_name: (JSON.parse(sessionStorage.getItem('google_user_info')!) as components['schemas']['GoogleUserInfo']).name ?? '',
      health_care_provider: true,
      country_code: null as Key | null,
      region_code: null as Key | null,
      npi_number: '',
      has_npi_number: true,
      doctor_specialty: '' as Key | null,
      newsletter: false,
      profession: '',
      hasAgreedToTerms: false,
      locationSearch: '',
    },
    onSubmit: async ({ value }) => {
      const { error } = await minDelay(tryCatch(createProfileMutation.mutateAsync({
        full_name: value.full_name.trim(),
        health_care_provider: value.health_care_provider,
        country_code: value.country_code!.toString(),
        region_code: value.region_code ? value.region_code.toString() : undefined,
        npi_number: value.npi_number === '' ? undefined : Number(value.npi_number),
        doctor_specialty: value.doctor_specialty
          ? value.country_code === 'US'
            ? value.doctor_specialty.toString()
            : NonUSDoctorSpecialtyEnum[value.doctor_specialty.toString() as keyof typeof NonUSDoctorSpecialtyEnum]
          : undefined,
        profession: value.profession ? value.profession.trim() : undefined,
        newsletter: value.newsletter,
      })), PROGRESS_INDICATOR_MIN_DELAY_DURATION);

      if (!error) {
        sessionStorage.removeItem('google_user_info');
        navigate({ to: '/sign-in' });
      }
    },
  });

  const debouncedLocation = useDebounce(useStore(form.store, (state) => state.values.locationSearch), SKELETON_MIN_DELAY_DURATION);
  const locationsQuery = useQuery(locationQueries.get({ search: debouncedLocation }));

  const npiNumber = useStore(form.store, (state) => state.values.npi_number);
  const getSpecialtyByNpiQuery = useQuery(specialtyQuery.getByNpi({ npi_number: npiNumber }));

  return (
    <div {...stylex.props(styles.base)}>
      <form
        id={id}
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
        {...stylex.props(formStyles.base)}>
        <Heading level={1} size={28}>Create Reader account</Heading>

        <form.Field name="full_name" validators={{ onSubmit: ProfileSchema.fullName }}>
          {(field) =>
            <TextField
              autoComplete="off"
              autoFocus
              id={field.name}
              isInvalid={field.state.meta.errors.length > 0}
              name={field.name}
              type="text"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={field.handleChange}>
              <LabeledInput label="Full name" />
              {field.state.meta.errors.length > 0 &&
                <Hint>
                  <Icon />
                  {field.state.meta.errors[0]?.message}
                </Hint>}
            </TextField>}
        </form.Field>

        <form.Field name="health_care_provider">
          {(field) =>
            <View style={segmentedButtonGroupStyles.container}>
              <SegmentedButtonGroup
                selectedKeys={[field.state.value ? 'yes' : 'no']}
                selectionMode="single"
                size={40}
                onSelectionChange={(keys) => {
                  field.handleChange(keys.keys().next().value === 'yes');
                  field.form.resetField('doctor_specialty');
                  field.form.resetField('profession');
                  field.form.resetField('has_npi_number');
                  field.form.resetField('npi_number');
                }}>
                <SegmentedButton id="yes" size={40}>Yes</SegmentedButton>
                <SegmentedButton id="no" size={40}>No</SegmentedButton>
              </SegmentedButtonGroup>
              <Text color="tertiary" size={16}>Are you a Healthcare Professional?</Text>
            </View>}
        </form.Field>

        <form.Field name="country_code" validators={{ onSubmit: ProfileSchema.countryCode }}>
          {(field) => {
            const isTouchedAndInvalid = field.getMeta().isTouched && field.state.meta.errors.length > 0;

            return (
              <ComboBox
                allowsEmptyCollection
                defaultFilter={() => true} // To return all regions if the country name is entered
                id={field.name}
                isInvalid={isTouchedAndInvalid}
                name={field.name}
                selectedKey={field.form.state.values.region_code ? `${field.state.value}-${field.form.state.values.region_code}` : field.state.value}
                size={48}
                onBlur={field.handleBlur}
                onInputChange={(value) => {
                  // Only start searching for a location if the value has at least 3 characters, so that not too many results are rendered at once
                  if (value && value.length >= 3 && value !== field.state.value) {
                    field.form.setFieldValue('locationSearch', value);
                  }
                }}
                onSelectionChange={(key) => {
                  if (!key) {
                    return;
                  }

                  const [countryCode, regionCode] = key.toString().split('-');

                  if (field.state.value === 'US' || countryCode === 'US') {
                    field.form.resetField('doctor_specialty');
                  }

                  if (countryCode !== 'US') {
                    field.form.resetField('npi_number');
                    field.form.resetField('has_npi_number');
                  }

                  field.form.setFieldValue('region_code', typeof regionCode === 'string' ? regionCode : null);

                  field.handleChange(countryCode);
                }}>
                {(renderProps) =>
                  <>
                    <LabeledInput label="Location">
                      {(renderProps) => renderProps.defaultChildren}
                    </LabeledInput>

                    {isTouchedAndInvalid &&
                      <Hint>
                        <Icon />
                        {field.state.meta.errors[0]?.message}
                      </Hint>}
                    <Popover isOpen={renderProps.isOpen && !!locationsQuery.data?.length}>
                      <Virtualizer
                        layout={ListLayout}
                        layoutOptions={{
                          rowHeight: 40,
                          padding: 0,
                          dropIndicatorThickness: 0,
                          gap: 0,
                        }}>
                        <ListBox items={locationsQuery.data ?? []}>
                          {({ name, code, regionName, regionCode }) =>
                            <ListBoxItem
                              id={regionCode ? `${code}-${regionCode}` : code}
                              size={40}
                              textValue={`${regionName || name}`}>
                              {regionName ?
                                <Text slot={LIST_BOX_ITEM_SLOTS.LABEL}>
                                  {regionName}&nbsp;
                                  <Text color="quaternary" size={12}>{name}</Text>
                                </Text> :
                                <Text slot={LIST_BOX_ITEM_SLOTS.LABEL}>{name}</Text>}
                            </ListBoxItem>}
                        </ListBox>
                      </Virtualizer>
                    </Popover>
                  </>}
              </ComboBox>
            );
          }}
        </form.Field>

        <form.Subscribe selector={(state) => [state.values.country_code, state.values.health_care_provider, state.values.has_npi_number] as const}>
          {([country_code, isHCP, hasNPINumber]) => country_code ? isHCP ?
            <>
              {country_code === 'US' &&
                <>
                  <View style={npiNumberStyles.container}>
                    <form.Field
                      name="npi_number"
                      validators={{
                        onChangeAsyncDebounceMs: 400,
                        onChangeAsync: async ({ value, fieldApi }) => {
                          if (!fieldApi.form.state.values.has_npi_number) {
                            return;
                          }

                          if (!value) {
                            return { message: 'Can\'t be empty' };
                          }

                          if (value.length !== 10) {
                            return { message: 'Enter a valid NPI number' };
                          }

                          const { data } = await tryCatch(getSpecialtyByNpiQuery.refetch());

                          if (!data?.data) {
                            return { message: 'Enter valid NPI number' };
                          }
                        },
                      }}>
                      {(field) =>
                        <TextField
                          autoComplete="off"
                          id={field.name}
                          isDisabled={!hasNPINumber}
                          isInvalid={field.state.meta.errors.length > 0}
                          name={field.name}
                          size={48}
                          style={npiNumberStyles.field}
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={field.handleChange}>
                          <LabeledInput label="NPI number" />
                          {field.state.meta.errors.length > 0 &&
                            <Hint>
                              <Icon />
                              {field.state.meta.errors[0]?.message}
                            </Hint>}
                        </TextField>}
                    </form.Field>

                    <form.Field name="has_npi_number">
                      {(field) =>
                        <TextButton
                          style={npiNumberStyles.noNpiTextButton}
                          variant="tertiary"
                          onPress={() => {
                            field.handleChange(!field.state.value);
                            field.form.resetField('npi_number');
                            field.form.resetField('doctor_specialty');
                          }}>
                          {field.state.value ? 'I don’t have an NPI number' : 'Use NPI number'}
                        </TextButton>}
                    </form.Field>
                  </View>
                  {getSpecialtyByNpiQuery.data &&
                    <TextField isDisabled value={getSpecialtyByNpiQuery.data ?? ''}>
                      <LabeledInput label="Specialty" />
                    </TextField>}
                </>}

              {country_code && !(country_code === 'US' && hasNPINumber) &&
                <form.Field name="doctor_specialty" validators={{ onSubmit: ProfileSchema.specialty }}>
                  {(field) => {
                    return country_code === 'US' ?
                      <TextField
                        autoComplete="off"
                        id={field.name}
                        isInvalid={field.state.meta.errors.length > 0}
                        name={field.name}
                        type="text"
                        value={field.state.value?.toString()}
                        onBlur={field.handleBlur}
                        onChange={field.handleChange}>
                        <LabeledInput label="Specialty" />
                        {field.state.meta.errors.length > 0 &&
                          <Hint>
                            <Icon />
                            {field.state.meta.errors[0]?.message}
                          </Hint>}
                      </TextField> :
                      <ComboBox
                        id={field.name}
                        isInvalid={field.state.meta.errors.length > 0}
                        name={field.name}
                        selectedKey={field.state.value}
                        size={48}
                        onBlur={field.handleBlur}
                        onSelectionChange={(key) => {
                          field.handleChange(key);
                        }}>
                        <LabeledInput label="Specialty" />
                        <Popover>
                          <ListBox items={Object.entries(NonUSDoctorSpecialtyEnum)}>
                            {([key, value]) => <ListBoxItem id={key} textValue={value}>{value}</ListBoxItem>}
                          </ListBox>
                        </Popover>
                        {field.state.meta.errors.length > 0 &&
                          <Hint>
                            <Icon />
                            {field.state.meta.errors[0]?.message}
                          </Hint>}
                      </ComboBox>;
                  }}
                </form.Field>}
            </> :
            <form.Field name="profession" validators={{ onSubmit: ProfileSchema.profession }}>
              {(field) =>
                <TextField
                  autoComplete="off"
                  id={field.name}
                  isInvalid={field.state.meta.errors.length > 0}
                  name={field.name}
                  type="text"
                  value={field.state.value?.toString()}
                  onBlur={field.handleBlur}
                  onChange={field.handleChange}>
                  <LabeledInput label="Profession" />
                  {field.state.meta.errors.length > 0 &&
                    <Hint>
                      <Icon />
                      {field.state.meta.errors[0]?.message}
                    </Hint>}
                </TextField>}
            </form.Field> : null
          }
        </form.Subscribe>

        <form.Field name="hasAgreedToTerms" validators={{ onSubmit: vLiteral(true) }}>
          {(field) => {
            const isTouchedAndInvalid = field.getMeta().isTouched && field.state.meta.errors.length > 0;

            return (
              <Checkbox
                id={field.name}
                isInvalid={isTouchedAndInvalid}
                isSelected={field.state.value}
                name={field.name}
                onBlur={field.handleBlur}
                onChange={field.handleChange}>
                I agree to the TrendMD <RouterLink _style={linkStyles.base} hash="terms-of-use" size={16} target="_blank" to="/privacy-and-terms/$">terms and conditions</RouterLink> and
                <RouterLink _style={linkStyles.base} hash="privacy-policy" size={16} target="_blank" to="/privacy-and-terms/$">privacy policy</RouterLink>
              </Checkbox>
            );
          }}
        </form.Field>

        <form.Field name="newsletter">
          {(field) =>
            <Checkbox
              id={field.name}
              isSelected={field.state.value}
              name={field.name}
              onBlur={field.handleBlur}
              onChange={field.handleChange}>
              I opt-in to receive TrendMD newsletter and personalized recommendations
            </Checkbox>}
        </form.Field>

        <form.Subscribe
          selector={(state) => [state.isSubmitting] as const}>
          {([isSubmitting]) =>
            <Button
              form={id}
              isPending={isSubmitting}
              isRounded
              size={48}
              type="submit">
              Sign up
            </Button>}
        </form.Subscribe>
      </form>
    </div>
  );
}

const styles = stylex.create({
  base: {
    display: 'flex',
    flex: '1 0 0',
    flexDirection: 'column',
    gap: 16,
    maxWidth: 432,
    paddingLeft: 20,
    paddingRight: 20,
    paddingTop: {
      default: 12,
      [breakpoints.large]: 32,
    },
    width: '100%',
  },
});

const formStyles = stylex.create({
  base: {
    alignSelf: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
});

const segmentedButtonGroupStyles = stylex.create({
  container: {
    alignItems: 'center',
    alignSelf: 'stretch',
    display: 'flex',
    gap: 12,
  },
});

const npiNumberStyles = stylex.create({
  container: {
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  field: {
    width: '100%',
  },
  noNpiTextButton: {
    color: $.textQuaternary,
    marginLeft: 14,
  },
});

const linkStyles = stylex.create({
  base: {
    color: 'inherit',
    textDecoration: 'underline',
  },
});
